import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Bookmark, Bug, CheckSquare, ChevronsUp, ChevronUp, Equal, ChevronDown, Layers, ListTree, UserX } from 'lucide-react';
import { Avatar, Menu } from '@vatman/ui';
import { AgentBadge } from '../../ui/AgentBadge';
import { canEnter, PRIORITIES, PRIORITY_COLORS, TYPES, TYPE_COLORS, type Issue, type IssueType, type Person, type ProjectTeam, type Sprint, type Status } from './model';

export function TypeIcon({ type, size = 15 }: { type: IssueType; size?: number }) {
  const color = TYPE_COLORS[type];
  const props = { size, color, strokeWidth: 2.2 };
  switch (type) {
    case 'bug':
      return <Bug {...props} />;
    case 'story':
      return <Bookmark {...props} />;
    case 'epic':
      return <Layers {...props} />;
    case 'subtask':
      return <ListTree {...props} />;
    default:
      return <CheckSquare {...props} />;
  }
}

export function PriorityIcon({ priority, size = 15 }: { priority: number; size?: number }) {
  const color = PRIORITY_COLORS[priority] ?? PRIORITY_COLORS[2];
  const props = { size, color, strokeWidth: 2.4 };
  if (priority === 0) return <ChevronsUp {...props} />;
  if (priority === 1) return <ChevronUp {...props} />;
  if (priority === 3) return <ChevronDown {...props} />;
  return <Equal {...props} />;
}

export function StatusBadge({ name, color }: { name: string; color?: string }) {
  return (
    <span className="tr-status" style={{ ['--c' as string]: color ?? '#8A8F98' }}>
      <i />
      {name}
    </span>
  );
}

export function Assignee({ issue, size = 22 }: { issue: Pick<Issue, 'assigneeName' | 'assigneeColor'>; size?: number }) {
  const { t } = useTranslation();
  if (!issue.assigneeName) {
    return (
      <span className="tr-nobody" title={t('tracker.unassigned')} style={{ width: size, height: size }}>
        <UserX size={size * 0.6} />
      </span>
    );
  }
  return <Avatar name={issue.assigneeName} color={issue.assigneeColor ?? '#8A8F98'} size={size} />;
}

/** A field value that opens a menu of choices. */
function Picker({ label, children, disabled, className }: { label: ReactNode; children: ReactNode; disabled?: boolean; className?: string }) {
  if (disabled) return <span className={`tr-pick ${className ?? ''}`}>{label}</span>;
  return (
    <Menu.Root>
      <Menu.Trigger asChild>
        <button className={`tr-pick ${className ?? ''}`} onClick={(e) => e.stopPropagation()}>
          {label}
        </button>
      </Menu.Trigger>
      <Menu.Content>{children}</Menu.Content>
    </Menu.Root>
  );
}

export function StatusPicker({ statuses, value, onChange, disabled }: { statuses: Status[]; value: string; onChange: (id: string) => void; disabled?: boolean }) {
  const cur = statuses.find((s) => s.id === value);
  return (
    <Picker disabled={disabled} label={<StatusBadge name={cur?.name ?? '—'} color={cur?.color} />}>
      {statuses.map((s) => (
        // A status the project's rules close from here is shown, but cannot be picked.
        <Menu.Item key={s.id} onSelect={() => onChange(s.id)} disabled={!canEnter(s, value)} keys={s.id === value ? ['✓'] : undefined}>
          <StatusBadge name={s.name} color={s.color} />
        </Menu.Item>
      ))}
    </Picker>
  );
}

export function AssigneePicker({ people, value, onChange, disabled, compact }: { people: Person[]; value: string | null; onChange: (id: string | null) => void; disabled?: boolean; compact?: boolean }) {
  const { t } = useTranslation();
  const cur = people.find((p) => p.id === value);
  const label = cur ? (
    <>
      <Avatar name={cur.name} color={cur.color} size={20} />
      {!compact && <span>{cur.name}</span>}
      {!compact && cur.agent && <AgentBadge />}
    </>
  ) : (
    <>
      <span className="tr-nobody" style={{ width: 20, height: 20 }}>
        <UserX size={12} />
      </span>
      {!compact && <span className="muted">{t('tracker.unassigned')}</span>}
    </>
  );
  return (
    <Picker disabled={disabled} label={label}>
      <Menu.Item onSelect={() => onChange(null)} keys={value === null ? ['✓'] : undefined}>
        {t('tracker.unassigned')}
      </Menu.Item>
      <Menu.Separator />
      {people.map((p) => (
        <Menu.Item key={p.id} icon={<Avatar name={p.name} color={p.color} size={18} />} onSelect={() => onChange(p.id)} keys={p.id === value ? ['✓'] : undefined}>
          {p.name}
          {p.agent && <AgentBadge />}
        </Menu.Item>
      ))}
    </Picker>
  );
}

export function PriorityPicker({ value, onChange, disabled, compact }: { value: number; onChange: (p: number) => void; disabled?: boolean; compact?: boolean }) {
  const { t } = useTranslation();
  return (
    <Picker
      disabled={disabled}
      label={
        <>
          <PriorityIcon priority={value} />
          {!compact && <span>{t(`tracker.priority.${value}`)}</span>}
        </>
      }
    >
      {PRIORITIES.map((p) => (
        <Menu.Item key={p} icon={<PriorityIcon priority={p} />} onSelect={() => onChange(p)} keys={p === value ? ['✓'] : undefined}>
          {t(`tracker.priority.${p}`)}
        </Menu.Item>
      ))}
    </Picker>
  );
}

export function TypePicker({ value, onChange, disabled, compact }: { value: IssueType; onChange: (t: IssueType) => void; disabled?: boolean; compact?: boolean }) {
  const { t } = useTranslation();
  return (
    <Picker
      disabled={disabled}
      label={
        <>
          <TypeIcon type={value} />
          {!compact && <span>{t(`tracker.type.${value}`)}</span>}
        </>
      }
    >
      {TYPES.map((ty) => (
        <Menu.Item key={ty} icon={<TypeIcon type={ty} />} onSelect={() => onChange(ty)} keys={ty === value ? ['✓'] : undefined}>
          {t(`tracker.type.${ty}`)}
        </Menu.Item>
      ))}
    </Picker>
  );
}

/** Whose an issue is, in a project of several teams (docs/teams-plan.md). */
export function TeamPicker({ teams, value, onChange, disabled }: { teams: ProjectTeam[]; value: string | null; onChange: (id: string | null) => void; disabled?: boolean }) {
  const { t } = useTranslation();
  const cur = teams.find((tm) => tm.id === value);
  return (
    <Picker disabled={disabled} label={<span className={cur ? '' : 'muted'}>{cur ? cur.name : t('tracker.noTeam')}</span>}>
      {teams.map((tm) => (
        <Menu.Item key={tm.id} onSelect={() => onChange(tm.id)} keys={tm.id === value ? ['✓'] : undefined}>
          {tm.name}
        </Menu.Item>
      ))}
    </Picker>
  );
}

export function SprintPicker({ sprints, value, onChange, disabled }: { sprints: Sprint[]; value: string | null; onChange: (id: string | null) => void; disabled?: boolean }) {
  const { t } = useTranslation();
  const open = sprints.filter((s) => s.state !== 'closed');
  const cur = sprints.find((s) => s.id === value);
  return (
    <Picker disabled={disabled} label={<span className={cur ? '' : 'muted'}>{cur ? cur.name : t('tracker.backlog')}</span>}>
      <Menu.Item onSelect={() => onChange(null)} keys={value === null ? ['✓'] : undefined}>
        {t('tracker.backlog')}
      </Menu.Item>
      {open.length > 0 && <Menu.Separator />}
      {open.map((s) => (
        <Menu.Item key={s.id} onSelect={() => onChange(s.id)} keys={s.id === value ? ['✓'] : undefined}>
          {s.name}
          {s.state === 'active' ? ` · ${t('tracker.sprintActive')}` : ''}
        </Menu.Item>
      ))}
    </Picker>
  );
}
